import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {ERROR, UNIT} from '@di-shared/const';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import EnergyMileageDailyGraph from '@di-shared/features/history/energy-usage/EnergyMileageDailyGraph';
import EnergyPowerDailyGraph from '@di-shared/features/history/energy-usage/EnergyPowerDailyGraph';
import {EnergyUsageDailyProps} from '@di-shared/features/history/energy-usage/EnergyUsageDaily/index';
import EnergyUsageTooltip from '@di-shared/features/history/energy-usage/EnergyUsageTooltip';
import {EV_DAILY_GRAPH_MAX_LENGTH, useEnergyUsageDaily} from '@di-shared/features/history/energy-usage/useEnergyUsageDaily';
import {roundToFirstDecimal} from '@di-shared/features/history/energy-usage/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';


export default function ({vin}: EnergyUsageDailyProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageDailyProps) {
  const ccId = environmentStore.getState().ccid;
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getMonthlyEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getMonthlyEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {isDailyEnergyUsageNone} = useEnergyUsageDaily({evDailyData});

  // No data 화면
  if (isDailyEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅

  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10184')}
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`,
          })}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('T10074_$energy', {
                  energy: 0 + UNIT.KWH,
                })}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.powerConsumption,
              scoreDate: item.date,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10037')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.avrMileage,
              scoreDate: item.date,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageDailyProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const ccId = environmentStore.getState().ccid;
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getMonthlyEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getMonthlyEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {verifiedEvDrivingInfoList, avgPowerConsumption, minPowerConsumption, maxPowerConsumption, maxAvrMileage, minAvrMileage} =
    useEnergyUsageDaily({evDailyData});

  const {convertEnergyEfficiencyValue, convertEnergyEfficiencySymbol} = useUnitConverter();
  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10073')}
          <EnergyUsageTooltip />
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[0].verifiedDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[verifiedEvDrivingInfoList.length - 1].verifiedDate, 'yMMMd')}`,
          })}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(roundToFirstDecimal(evDailyData.evStatistics.totalPowerConsumption))}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('T10074_$energy', {
                  energy: formatNumberWithCommas(roundToFirstDecimal(+avgPowerConsumption)) + UNIT.KWH,
                })}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={verifiedEvDrivingInfoList.map((item) => ({
              score: roundToFirstDecimal(item.powerConsumption),
              scoreDate: item.verifiedDate,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(roundToFirstDecimal(maxPowerConsumption))}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(roundToFirstDecimal(minPowerConsumption))}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10037')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(convertEnergyEfficiencyValue(evDailyData.evStatistics.avrMileage, {skipFormatting: true}))}
                <span className="body_small_r">
                  {convertEnergyEfficiencySymbol(evDailyData.evStatistics.avrMileage, {skipFormatting: true})}
                </span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={verifiedEvDrivingInfoList.map((item) => ({
              score: item.avrMileage,
              scoreDate: item.verifiedDate,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">
                {formatNumberWithCommas(convertEnergyEfficiencyValue(maxAvrMileage, {skipFormatting: true}))}
              </span>
              <span className="statistics_info_unit">{convertEnergyEfficiencySymbol(maxAvrMileage, {skipFormatting: true})}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">
                {formatNumberWithCommas(convertEnergyEfficiencyValue(minAvrMileage, {skipFormatting: true}))}
              </span>
              <span className="statistics_info_unit">{convertEnergyEfficiencySymbol(minAvrMileage, {skipFormatting: true})}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(roundToFirstDecimal(evDailyData.evStatistics.totalRegenPower))}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('T10074_$energy', {
                  energy: ERROR.DASH + UNIT.KWH,
                })}
              </span>
            </span>
          </div>
        </div>
        <DailyLineGraphError />
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10037')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <DailyLineGraphError />
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅
  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10073')}
          <EnergyUsageTooltip />
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`,
          })}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10071')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('T10074_$energy', {
                  energy: ERROR.DASH + UNIT.KWH,
                })}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.powerConsumption,
              scoreDate: item.date,
            }))}
            error={true}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('T10037')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.avrMileage,
              scoreDate: item.date,
            }))}
            error={true}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10075')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li className="statistics_info_item">
            <span className="statistics_info_key">{t('T10076')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('T10072')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
